import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import PropTypes from "prop-types";

const BookingContext = createContext();

const DATE_STORAGE_KEY = "selectedBookingDates";
const TIME_STORAGE_KEY = "trailerPickupTime";

/**
 * Oletuspäivät:
 * - aloitus = tänään klo 12
 * - lopetus = huomenna klo 12
 */
const getInitialDates = () => {
  const now = new Date();
  now.setHours(12, 0, 0, 0);

  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  return {
    startDate: now.toISOString(),
    endDate: tomorrow.toISOString(),
  };
};

export const BookingProvider = ({ children }) => {
  // =========================
  // 📅 PÄIVÄT (vrk-vuokra)
  // =========================
  const [selectedDates, setSelectedDates] = useState(() => {
    const saved = localStorage.getItem(DATE_STORAGE_KEY);
    if (!saved) return getInitialDates();

    try {
      const parsed = JSON.parse(saved);
      if (!parsed?.startDate || !parsed?.endDate) {
        return getInitialDates();
      }

      const start = new Date(parsed.startDate);
      start.setHours(12, 0, 0, 0);

      // Menneet päivät → reset
      if (start < new Date()) {
        return getInitialDates();
      }

      return parsed;
    } catch {
      return getInitialDates();
    }
  });

  // Tallennus localStorageen
  useEffect(() => {
    if (selectedDates?.startDate && selectedDates?.endDate) {
      localStorage.setItem(
        DATE_STORAGE_KEY,
        JSON.stringify(selectedDates)
      );
    }
  }, [selectedDates]);

  // =========================
  // 🧮 LASKUTETTAVAT VUOROKAUDET
  // =========================
  const billableDays = useMemo(() => {
    if (!selectedDates?.startDate || !selectedDates?.endDate) return 0;

    const start = new Date(selectedDates.startDate);
    const end = new Date(selectedDates.endDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const diffMs = end - start;
    if (diffMs <= 0) return 0;

    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [selectedDates]);

  // =========================
  // ⏱ PERÄKÄRRY / NOUTOAIKA
  // =========================
  const [trailerPickupTime, setTrailerPickupTime] = useState(() => {
    return localStorage.getItem(TIME_STORAGE_KEY) || "07:00";
  });

  useEffect(() => {
    if (trailerPickupTime) {
      localStorage.setItem(TIME_STORAGE_KEY, trailerPickupTime);
    }
  }, [trailerPickupTime]);

  return (
    <BookingContext.Provider
      value={{
        selectedDates,
        setSelectedDates,
        billableDays,
        trailerPickupTime,
        setTrailerPickupTime,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
};

BookingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useBooking = () => useContext(BookingContext);
